import {
  RefObject, useLayoutEffect, useState,
} from 'react';

// Largura mínima: garante que os números (ex.: "-12.3") caibam sem quebrar.
export const MIN_CELL_WIDTH = 90;
// Largura máxima: com poucos dados a célula cresce até aqui e para — evita
// quadrados gigantes/feios numa tela grande.
export const MAX_CELL_WIDTH = 120;

/**
 * Calcula a largura ideal das células de dados para que a tabela ocupe o maior
 * espaço horizontal possível sem ficar horrível:
 *   - muitos dados → células no mínimo (tabela rola horizontalmente);
 *   - poucos dados + tela grande → células no máximo (tabela centralizada);
 *   - caso intermediário → largura que preenche exatamente o container.
 *
 * A largura da coluna de rótulos (linha) é variável, então é medida em runtime;
 * o restante do espaço é dividido igualmente entre as `columnCount` colunas de
 * dados. Recalcula ao redimensionar o container (ResizeObserver) e quando o
 * número de colunas ou os rótulos das linhas mudam (`layoutKey`).
 */
export default function useAdaptiveCellWidth(
  containerRef: RefObject<HTMLElement | null>,
  rowHeaderRef: RefObject<HTMLElement | null>,
  columnCount: number,
  layoutKey: string,
): number {
  const [cellWidth, setCellWidth] = useState<number>(MIN_CELL_WIDTH);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || columnCount <= 0) return undefined;

    const measure = () => {
      const available = container.clientWidth;
      const rowHeaderWidth = rowHeaderRef.current?.offsetWidth ?? 0;
      const raw = Math.floor((available - rowHeaderWidth) / columnCount);
      setCellWidth(Math.max(MIN_CELL_WIDTH, Math.min(MAX_CELL_WIDTH, raw)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, rowHeaderRef, columnCount, layoutKey]);

  return cellWidth;
}
